import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import PhaseAnimation from '../components/PhaseAnimation';
import IndagaAnim from '../components/IndagaAnim';
import SocialLinks from '../components/SocialLinks';
import VerificaDominio from '../components/VerificaDominio';
import { useAuth } from '../auth';

const ESITO_COL = {
  'TIM': 'var(--err)', 'NON TIM': 'var(--ok)', 'ERRORE': 'var(--err)',
  'SERVIZIO_NON_DISPONIBILE': 'var(--warn)',
};
const esitoLabel = (r) => r === 'SERVIZIO_NON_DISPONIBILE' ? 'Servizio non disponibile' : (r || '—');

const FASE_LABEL = {
  avvio: 'Avvio verifica…',
  registrazione: 'Chiamata al 456 e registrazione…',
  trascrizione: 'Trascrizione della risposta…',
  attesa: 'Attesa tra le varianti…',
  analisi: 'Analisi dell\'esito…',
  standby: 'Servizio in standby…',
};

// chip GNR piccolo e neutro (niente viola)
function GnrTag({ varianti, small }) {
  return (
    <span style={{
      padding: small ? '1px 7px' : '3px 10px', borderRadius: 14, fontSize: small ? 10 : 12, fontWeight: 700,
      background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--line)', whiteSpace: 'nowrap',
    }}>
      GNR{varianti ? ` · ${varianti} varianti` : ''}
    </span>
  );
}

export default function RicercaOnlinePage() {
  const [numero, setNumero] = useState('');
  const [busy, setBusy] = useState(false);
  const [fase, setFase] = useState('avvio');
  const [stato, setStato] = useState(null);
  const [gnr, setGnr] = useState(null);     // {is_gnr, varianti} — noto SUBITO dalla POST
  const [esito, setEsito] = useState(null);
  const [regStato, setRegStato] = useState(null);   // null | 'scaricata' | 'eliminata'
  const [crono, setCrono] = useState([]);
  // agente Indaga (solo admin)
  const [indagaOpen, setIndagaOpen] = useState(false);
  const [indagaBusy, setIndagaBusy] = useState(false);
  const [indagaData, setIndagaData] = useState(null);
  const [creaBusy, setCreaBusy] = useState(false);
  const [creaMsg, setCreaMsg] = useState(null);     // {type:'ok'|'exists', text}
  const poll = useRef(null);
  const { user, can } = useAuth();
  const isAdmin = user?.ruolo === 'admin';

  const loadCrono = useCallback(() => {
    // admin: tutta la cronologia · operatore: solo le sue (lo scoping lo fa il backend)
    axios.get(`${API}/ricerca-online/cronologia?limit=200`).then(r => setCrono(r.data?.items || [])).catch(() => {});
  }, []);
  useEffect(() => { loadCrono(); return () => clearInterval(poll.current); }, [loadCrono]);

  const avvia = async () => {
    const n = numero.trim();
    if (!n) { toast.info('Inserisci un numero'); return; }
    setBusy(true); setEsito(null); setGnr(null); setFase('avvio'); setStato('in_coda'); setRegStato(null);
    try {
      const r = await axios.post(`${API}/ricerca-online`, { numero: n });
      setGnr({ is_gnr: r.data.is_gnr, varianti: r.data.varianti });
      const id = r.data.id;
      poll.current = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/ricerca-online/${id}`);
          setFase(s.data.fase || 'avvio'); setStato(s.data.stato);
          if (s.data.stato === 'completato' || s.data.stato === 'errore') {
            clearInterval(poll.current);
            setEsito(s.data); setBusy(false); loadCrono();
            const ris = s.data.risultato;
            if (ris === 'TIM' || ris === 'NON TIM') toast.success(`Esito: ${ris}`);
            else toast.warning(`Esito: ${esitoLabel(ris)}`);
          }
        } catch { /* continua a interrogare */ }
      }, 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore verifica');
      setBusy(false);
    }
  };

  const scaricaReg = async (id, numero) => {
    try {
      const r = await axios.get(`${API}/ricerca-online/${id}/registrazione`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `verifica_${numero}.mp3`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      setRegStato('scaricata');
    } catch {
      toast.error('Registrazione non disponibile');
      setRegStato('eliminata');
    }
  };

  const eliminaReg = async (id) => {
    try { await axios.delete(`${API}/ricerca-online/${id}/registrazione`); } catch { /* idempotente */ }
    setRegStato('eliminata');
  };

  const avviaIndaga = async () => {
    if (!esito?.numero) return;
    setIndagaBusy(true); setIndagaData(null); setIndagaOpen(true); setCreaMsg(null);
    try {
      const r = await axios.post(`${API}/indaga`, { numero: esito.numero });
      setIndagaData(r.data);
    } catch (e) {
      setIndagaData({ trovato: false, errore: e.response?.data?.detail || 'Errore Indaga' });
    }
    setIndagaBusy(false);
  };
  const creaRecord = async (force = false) => {
    if (!indagaData) return;
    setCreaBusy(true);
    try {
      const r = await axios.post(`${API}/indaga/crea-record`, {
        numero: esito.numero, risultato: esito.risultato, numero_verificato: esito.numero_verificato,
        ragione_sociale: indagaData.ragione_sociale, indirizzo: indagaData.indirizzo, comune: indagaData.comune,
        partita_iva: indagaData.partita_iva, email: indagaData.email, social: indagaData.social,
        maps_categoria: indagaData.categoria, maps_url: indagaData.maps_url,
        force,
      });
      if (r.data.exists) setCreaMsg({ type: 'exists', text: 'Il numero esiste già in archivio.' });
      else { setCreaMsg({ type: 'ok', text: r.data.updated ? 'Record aggiornato.' : 'Record creato in archivio.' }); toast.success(r.data.updated ? 'Record aggiornato' : 'Record creato'); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore creazione record'); }
    setCreaBusy(false);
  };

  const faseTxt = stato === 'in_coda'
    ? 'In coda — la richiesta ha priorità, parte appena si libera una linea…'
    : (FASE_LABEL[fase] || 'Verifica in corso…');

  // ── widget cronologia: per link e per fascia oraria ──
  const perLink = (() => {
    const m = new Map();
    crono.forEach(c => {
      const k = c.via === 'pubblico' ? (c.nome || 'link') : (c.user || 'interno');
      m.set(k, (m.get(k) || 0) + 1);
    });
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  })();
  const maxLink = Math.max(1, ...perLink.map(([, n]) => n));
  const FASCE = [['Notte', '00–06', 0, 6], ['Mattina', '06–12', 6, 12], ['Pomeriggio', '12–18', 12, 18], ['Sera', '18–24', 18, 24]];
  const perFascia = FASCE.map(([nome, range, a, b]) => ({
    nome, range,
    n: crono.filter(c => { if (!c.at) return false; const h = new Date(c.at).getHours(); return h >= a && h < b; }).length,
  }));
  const maxFascia = Math.max(1, ...perFascia.map(f => f.n));

  return (
    <div style={{ padding: 20, maxWidth: 720, margin: '0 auto' }}>
      <h1 style={{ fontSize: 20, margin: '0 0 4px' }}>🔎 Verifica numero (online)</h1>
      <p style={{ margin: '0 0 18px', color: 'var(--muted)', fontSize: 13 }}>
        Inserisci un numero di rete fissa e avvia la verifica TIM / non TIM tramite il servizio 456.
        Il numero non viene archiviato: resta solo nella cronologia delle ricerche.
      </p>

      <VerificaDominio />

      <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: 20, marginBottom: 18 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <input
            value={numero}
            onChange={e => setNumero(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !busy) avvia(); }}
            placeholder="Es. 0612345678"
            disabled={busy}
            style={{ flex: '1 1 240px', padding: '12px 14px', fontSize: 16, border: '1px solid var(--line)', borderRadius: 10 }}
          />
          <button className="btn btn-primary" style={{ padding: '0 22px', fontSize: 15 }} disabled={busy} onClick={avvia}>
            {busy ? 'Verifica…' : 'Avvia verifica'}
          </button>
        </div>

        {/* GNR noto subito */}
        {busy && gnr?.is_gnr && (
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', fontSize: 12, color: 'var(--muted)' }}>
            <GnrTag varianti={gnr.varianti} />
            <span>numerazione a blocchi: verrà provata su più varianti, può richiedere qualche tentativo.</span>
          </div>
        )}

        {busy && (
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <div className="ricerca-anim" style={{ display: 'flex', justifyContent: 'center' }}>
              <PhaseAnimation fase={fase} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0c2f53', margin: '10px 0 4px' }}>{faseTxt}</div>
            <div style={{ fontSize: 11, color: 'var(--faint)' }}>Può richiedere 1–2 minuti.</div>
          </div>
        )}

        {!busy && esito && (
          <div style={{ marginTop: 18, textAlign: 'center' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
              <span style={{
                display: 'inline-block', padding: '10px 22px', borderRadius: 30, fontSize: 20, fontWeight: 800,
                color: '#fff', background: ESITO_COL[esito.risultato] || 'var(--muted)',
              }}>
                {esitoLabel(esito.risultato)}
              </span>
              {esito.is_gnr && <GnrTag varianti={esito.varianti} />}
            </div>
            <div style={{ marginTop: 12, fontSize: 13, color: 'var(--muted)' }}>
              Numero: <b>{esito.numero}</b>
              {esito.numero_verificato && esito.numero_verificato !== esito.numero && <> → verificato su <b>{esito.numero_verificato}</b></>}
            </div>
            {esito.errore && <div style={{ marginTop: 6, fontSize: 12, color: 'var(--err)' }}>{esito.errore}</div>}
            {esito.audio_file && !regStato && (
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--surface-2)' }}>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Registrazione della chiamata disponibile</div>
                <button className="btn btn-sm btn-primary" style={{ marginRight: 8 }}
                  onClick={() => scaricaReg(esito.id, esito.numero)}>📥 Scarica registrazione</button>
                <button className="btn btn-sm" onClick={() => eliminaReg(esito.id)}>🗑 Non scaricare</button>
              </div>
            )}
            {regStato === 'scaricata' && (
              <div style={{ marginTop: 12, fontSize: 12, color: 'var(--ok)' }}>Registrazione scaricata ed eliminata.</div>
            )}
            {regStato === 'eliminata' && (
              <div style={{ marginTop: 12, fontSize: 12, color: 'var(--faint)' }}>Registrazione eliminata.</div>
            )}

            {(isAdmin || can('indaga')) && (esito.risultato === 'TIM' || esito.risultato === 'NON TIM') && (
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--surface-2)' }}>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Vuoi sapere a chi appartiene questo numero?</div>
                <button className="btn btn-sm btn-secondary" onClick={avviaIndaga}>Avvia agente Indaga</button>
              </div>
            )}
          </div>
        )}
      </div>

      {indagaOpen && (
        <div className="nt-overlay" onClick={() => !indagaBusy && setIndagaOpen(false)} style={{ zIndex: 10000 }}>
          <div className="nt-detail" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <div className="nt-detail-head">
              <span>AI Indaga — {esito?.numero}</span>
              <button className="ob-x" onClick={() => setIndagaOpen(false)}>✕</button>
            </div>
            <div className="nt-detail-body">
              {indagaBusy ? (
                <IndagaAnim />
              ) : !indagaData || !indagaData.trovato ? (
                <div style={{ color: 'var(--faint)', fontSize: 13 }}>{indagaData?.errore || 'Nessun risultato su Google Maps per questo numero.'}</div>
              ) : (
                <>
                  <div className="nt-d-grid">
                    <div><label>Ragione sociale</label><b>{indagaData.ragione_sociale || '—'}</b></div>
                    <div><label>Categoria</label>{indagaData.categoria || '—'}</div>
                    <div><label>Indirizzo</label>{indagaData.indirizzo || '—'}</div>
                    <div><label>Comune</label>{indagaData.comune || '—'}</div>
                    <div><label>P. IVA</label>{indagaData.partita_iva || <span style={{ color: 'var(--faint)' }}>non certa</span>}</div>
                    <div><label>Sito</label>{indagaData.sito ? <a href={indagaData.sito} target="_blank" rel="noreferrer">apri</a> : '—'}</div>
                    <div><label>Email</label>{indagaData.email ? <a href={`mailto:${indagaData.email}`}>{indagaData.email}</a> : <span style={{ color: 'var(--faint)' }}>—</span>}</div>
                  </div>
                  {indagaData.social && Object.keys(indagaData.social).length > 0 && (
                    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: '.04em' }}>Social</span>
                      <SocialLinks social={indagaData.social} size={22} gap={10} />
                    </div>
                  )}
                  {indagaData.maps_url && <div style={{ marginTop: 8 }}><a href={indagaData.maps_url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>Vedi su Google Maps →</a></div>}
                  {creaMsg && <div style={{ marginTop: 10, fontSize: 13, color: creaMsg.type === 'ok' ? 'var(--ok)' : 'var(--warn)' }}>{creaMsg.text}</div>}
                  <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
                    {creaMsg?.type === 'exists' ? (
                      <button className="btn btn-secondary" disabled={creaBusy} onClick={() => creaRecord(true)}>{creaBusy ? '…' : 'Aggiorna esistente'}</button>
                    ) : creaMsg?.type === 'ok' ? (
                      <button className="btn btn-secondary" onClick={() => setIndagaOpen(false)}>Chiudi</button>
                    ) : (
                      <button className="btn btn-primary" disabled={creaBusy} onClick={() => creaRecord(false)}>{creaBusy ? '…' : 'Crea record in archivio'}</button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: 16 }}>
        <h3 style={{ margin: '0 0 10px', fontSize: 14 }}>{isAdmin ? 'Cronologia ricerche' : 'Le tue ricerche online'}</h3>
        {!isAdmin && (
          <p style={{ margin: '-4px 0 10px', fontSize: 12, color: 'var(--faint)' }}>
            Le tue verifiche + quelle fatte tramite i link pubblici che hai creato.
          </p>
        )}
        {crono.length === 0 ? (
          <div style={{ color: 'var(--faint)', fontSize: 13 }}>Nessuna ricerca ancora.</div>
        ) : (
          <>
            {/* widget intorno alla lista */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
              <div style={{ flex: '1 1 240px', minWidth: 220, border: '1px solid var(--line)', borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>
                  Per link · {crono.length} ricerche
                </div>
                {perLink.map(([k, n]) => (
                  <div key={k} style={{ marginBottom: 7 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2, gap: 8 }}>
                      <span style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{k}</span>
                      <b>{n}</b>
                    </div>
                    <div style={{ height: 6, background: 'var(--surface-2)', borderRadius: 4 }}>
                      <div style={{ width: `${(n / maxLink) * 100}%`, height: '100%', background: '#4895ef', borderRadius: 4 }} />
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ flex: '1 1 240px', minWidth: 220, border: '1px solid var(--line)', borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>
                  Per fascia oraria
                </div>
                {perFascia.map(f => (
                  <div key={f.nome} style={{ marginBottom: 7 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                      <span>{f.nome} <span style={{ color: 'var(--faint)' }}>{f.range}</span></span><b>{f.n}</b>
                    </div>
                    <div style={{ height: 6, background: 'var(--surface-2)', borderRadius: 4 }}>
                      <div style={{ width: `${(f.n / maxFascia) * 100}%`, height: '100%', background: 'var(--ok)', borderRadius: 4 }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* lista: scrolla DENTRO la sezione, non la pagina */}
            <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--surface-2)', borderRadius: 10 }}>
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1, boxShadow: '0 1px 0 var(--line)' }}>
                  <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
                    <th style={{ padding: '8px 10px' }}>Data</th><th>Numero</th><th>Esito</th><th>Origine</th>
                  </tr>
                </thead>
                <tbody>
                  {crono.map(c => (
                    <tr key={c.id} style={{ borderTop: '1px solid var(--surface-2)' }}>
                      <td style={{ whiteSpace: 'nowrap', padding: '6px 10px' }}>{c.at ? new Date(c.at).toLocaleString('it-IT') : '—'}</td>
                      <td>
                        <code>{c.numero}</code>
                        {c.is_gnr && <span style={{ marginLeft: 6 }}><GnrTag small /></span>}
                      </td>
                      <td style={{ fontWeight: 700, color: ESITO_COL[c.risultato] || 'var(--muted)' }}>{esitoLabel(c.risultato)}</td>
                      <td style={{ color: 'var(--muted)' }}>
                        {c.nome || c.user || '—'}
                        {c.via === 'pubblico' && (
                          <span style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 12, fontSize: 10,
                            background: 'var(--surface-2)', color: 'var(--info)', border: '1px solid #e0e7ff' }}>🔗 link</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
